import { useEffect, useState } from 'react';
import { useCustomer } from '../../../lib/customer';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { PortalContainer } from '../PortalShell';

type Product = {
  id: string;
  sku: string;
  brand_code: string;
  name: string;
  msrp_mxn: number | null;
  tags: string[];
};

export function PortalPrecios() {
  const { customer } = useCustomer();
  const [products, setProducts] = useState<Product[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('products_public')
      .select('id, sku, brand_code, name, msrp_mxn, tags')
      .eq('status', 'active')
      .order('msrp_mxn', { ascending: false, nullsFirst: false })
      .limit(500)
      .then(({ data }) => {
        setProducts((data as Product[]) ?? []);
        setLoading(false);
      });
  }, []);

  if (!customer) return null;

  const margin = customer.tier_discount_pct ?? 0;
  const certified = new Set(customer.lutron_lines_certified);

  const filtered = products.filter(p => {
    // Para Lutron: solo mostrar líneas certificadas
    if (p.brand_code === 'lutron') {
      const lineTag = (p.tags ?? []).find(t => t.startsWith('lutron:'));
      if (lineTag) {
        const line = lineTag.slice(7);
        if (!certified.has(line)) return false;
      }
    }
    // Search
    if (q.trim()) {
      const qn = q.trim().toLowerCase();
      return p.name.toLowerCase().includes(qn) || p.sku.toLowerCase().includes(qn);
    }
    return true;
  });

  return (
    <PortalContainer title="Mis precios" eyebrow={`${customer.tier_name ?? 'Tier'} · Margen ${margin}%`}>
      <div style={{
        background: theme.bgFooter,
        borderLeft: `4px solid ${theme.brandTag.lutron}`,
        padding: 12,
        marginBottom: 16,
        fontSize: 12,
        color: theme.textMuted,
        lineHeight: 1.6,
      }}>
        Las líneas Lutron sin certificar <strong>no aparecen en tu lista</strong>.
        {customer.lutron_lines_certified.length > 0 ? (
          <> Tienes {customer.lutron_lines_certified.length} de 6 líneas certificadas.</>
        ) : (
          <> Aún no estás certificado en ninguna línea Lutron — ve a Capacitación.</>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Buscar SKU o nombre del producto…"
          style={{
            flex: 1,
            minWidth: 240,
            padding: '12px 14px',
            border: `1px solid ${theme.border}`,
            fontSize: 14,
            outline: 'none',
            color: theme.text,
            fontFamily: theme.font.sans,
          }}
        />
      </div>

      {loading ? (
        <div style={{ padding: 20, color: theme.textMuted }}>Cargando…</div>
      ) : (
        <>
          <div style={{ marginBottom: 8, fontSize: 12, color: theme.textMuted }}>
            {filtered.length} de {products.length} productos
          </div>
          <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
              <thead>
                <tr style={{ background: theme.bgSoft }}>
                  <Th>SKU</Th>
                  <Th>Producto</Th>
                  <Th>Marca</Th>
                  <Th style={{ textAlign: 'right' }}>PVP</Th>
                  <Th style={{ textAlign: 'right' }}>Mi precio</Th>
                  <Th style={{ textAlign: 'right' }}>Ahorro</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 200).map(p => {
                  const myPrice = p.msrp_mxn != null ? p.msrp_mxn * (1 - margin / 100) : null;
                  return (
                    <tr key={p.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <Td><span style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>{p.sku}</span></Td>
                      <Td>{p.name}</Td>
                      <Td>
                        <span style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '2px 8px',
                          background: p.brand_code === 'lutron' ? 'rgba(192,132,252,0.15)' : 'rgba(87,255,154,0.18)',
                          color: p.brand_code === 'lutron' ? theme.brandTag.lutron : theme.accentDark,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}>
                          {p.brand_code}
                        </span>
                      </Td>
                      <Td style={{ textAlign: 'right', color: theme.textMuted, textDecoration: 'line-through' }}>
                        {p.msrp_mxn != null ? `$${Number(p.msrp_mxn).toLocaleString('es-MX', { maximumFractionDigits: 0 })}` : '—'}
                      </Td>
                      <Td style={{ textAlign: 'right', color: theme.brand, fontWeight: 700 }}>
                        {myPrice != null ? `$${myPrice.toLocaleString('es-MX', { maximumFractionDigits: 0 })}` : '—'}
                      </Td>
                      <Td style={{ textAlign: 'right' }}>
                        {margin > 0 && p.msrp_mxn != null && (
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: '3px 8px',
                            background: 'rgba(87,255,154,0.18)', color: theme.accentDark,
                          }}>
                            -{margin}%
                          </span>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PortalContainer>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left',
      padding: '12px 14px',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: theme.textMuted,
      borderBottom: `1px solid ${theme.border}`,
      ...style,
    }}>
      {children}
    </th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '10px 14px', verticalAlign: 'top', ...style }}>{children}</td>;
}
